import type { QueryClient } from "@tanstack/react-query";
import type { AnyRouter } from "@tanstack/react-router";
import { meQuery } from "./session.ts";

/**
 * What makes the app feel instant, in one place (docs/perf.md has the numbers behind each):
 *
 * - `staleWhileRevalidate`: a route's loader returns at once when the page's data is already in memory, and the
 *   refresh happens behind the scenes; without data it waits at most `HOLD_MS`, then the page shows and fills in.
 *   Before, the old page stayed under the new address until every read had answered.
 * - `startFirstScreen`: the first screen's code starts loading together with the session check, instead of
 *   after it.
 * - `preloadOnIntent`: plain `<a href>` links (the shell's nav, rows and cards in `@adea/ui`) fetch their page's
 *   code and data when the pointer rests on them or they get focus, the way the router's `<Link>` does.
 */

/**
 * How long a click keeps the current page while the next one's data arrives. Data that comes within it shows
 * complete; anything slower shows the new page at once (its bar and title), with a placeholder where data is
 * missing (which itself waits ~150 ms before it shows, see styles.css). The address and the page never disagree
 * for longer than this.
 */
const HOLD_MS = 120;

/** Loaders call `prefetchQuery`: with data cached it returns at once; without, it waits at most `HOLD_MS`. */
export function staleWhileRevalidate(queryClient: QueryClient): void {
  const prefetch = queryClient.prefetchQuery.bind(queryClient);
  queryClient.prefetchQuery = ((options: Parameters<typeof prefetch>[0]) => {
    const cached = queryClient.getQueryState(options.queryKey)?.data !== undefined;
    const done = prefetch(options);
    if (cached) return Promise.resolve();
    return Promise.race([done, new Promise<void>((resolve) => setTimeout(resolve, HOLD_MS))]);
  }) as typeof queryClient.prefetchQuery;
}

/** The session check and the first screen's code, side by side (`index.html` already asked for `/api/v1/me`). */
export function startFirstScreen(router: AnyRouter, queryClient: QueryClient): void {
  void queryClient.prefetchQuery(meQuery);
  try {
    const [routes] = router.getMatchedRoutes(router.latestLocation.pathname);
    for (const route of routes) void router.loadRouteChunk(route)?.catch(() => undefined);
  } catch {
    // the router loads them itself
  }
}

/** How long the pointer rests on a link before its page is fetched: long enough to skip links it only crosses. */
const HOVER_MS = 60;

function internalHref(target: EventTarget | null): string | null {
  const a = target instanceof Element ? target.closest("a[href]") : null;
  if (!(a instanceof HTMLAnchorElement)) return null;
  if (a.target && a.target !== "_self") return null;
  if (a.hasAttribute("download") || a.dataset.noPreload !== undefined) return null;
  const href = a.getAttribute("href") ?? "";
  if (!href.startsWith("/") || href.startsWith("//")) return null;
  if (/^\/(api|mcp|oauth|webhooks|\.well-known)(\/|$)/.test(href)) return null;
  return href;
}

export function preloadOnIntent(router: AnyRouter): void {
  if (typeof document === "undefined") return;
  const recent = new Map<string, number>();
  const preload = (href: string) => {
    const now = Date.now();
    // a link crossed again within a few seconds: its page is already on its way
    if (now - (recent.get(href) ?? 0) < 5_000) return;
    recent.set(href, now);
    if (recent.size > 200) recent.clear();
    const url = new URL(href, window.location.origin);
    if (url.pathname === window.location.pathname && url.search === window.location.search) return;
    void Promise.resolve()
      .then(() =>
        router.preloadRoute({
          to: url.pathname,
          search: Object.fromEntries(url.searchParams),
        } as never),
      )
      .catch(() => undefined);
  };

  let timer: number | undefined;
  let pending: string | null = null;
  document.addEventListener(
    "pointerover",
    (e) => {
      if (e.pointerType === "touch") return;
      const href = internalHref(e.target);
      if (href === pending) return;
      window.clearTimeout(timer);
      pending = href;
      if (href) timer = window.setTimeout(() => preload(href), HOVER_MS);
    },
    { passive: true },
  );
  document.addEventListener(
    "pointerout",
    (e) => {
      if (internalHref(e.relatedTarget) === pending) return;
      window.clearTimeout(timer);
      pending = null;
    },
    { passive: true },
  );
  // A finger on a link, or the keyboard reaching it: the page is fetched right away.
  const now = (e: Event) => {
    const href = internalHref(e.target);
    if (href) preload(href);
  };
  document.addEventListener("touchstart", now, { passive: true });
  document.addEventListener("focusin", now);
}
